import React, { useState, useRef, useEffect } from 'react';
import { editMessage, deleteMessage } from '../services/api';

function ChatArea({ 
  currentChat, 
  messages = [], 
  onSendMessage, 
  user, 
  onCloseChat, 
  isUserOnline, 
  usersData = {},
  isMobile = false,
  onOpenSidebar = null
}) {
  const [input, setInput] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [menuVisible, setMenuVisible] = useState(null);
  const messagesEndRef = useRef(null);
  const editInputRef = useRef(null);

  const chatAvatar = currentChat ? (usersData[currentChat.username]?.avatar || '👤') : '👤';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (editingMessage && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingMessage]);

  const handleSend = (e) => {
    e.preventDefault();
    if (input.trim()) {
      onSendMessage(input);
      setInput('');
    }
  };

  const handleEditMessage = async (msg) => {
    setEditingMessage(msg.id);
    setEditingText(msg.text);
    setMenuVisible(null);
  };

  const handleSaveEdit = async (msgId) => {
    if (!editingText.trim()) {
      alert('Сообщение не может быть пустым');
      return;
    }
    
    try {
      await editMessage(msgId, editingText.trim());
      alert('✅ Сообщение изменено');
      setEditingMessage(null);
      setEditingText('');
      window.location.reload();
    } catch (err) {
      console.error('Ошибка редактирования:', err);
      alert('❌ Ошибка редактирования: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditingText('');
  };

  const handleDeleteMessage = async (msg) => {
    if (!window.confirm('🗑️ Удалить это сообщение?')) {
      setMenuVisible(null);
      return;
    }
    
    try {
      await deleteMessage(msg.id);
      alert('✅ Сообщение удалено');
      setMenuVisible(null);
      window.location.reload();
    } catch (err) {
      console.error('Ошибка удаления:', err);
      alert('❌ Ошибка удаления: ' + (err.response?.data?.error || err.message));
    }
  };

  const canEditDelete = (msg) => {
    return user && msg.from === user.username;
  };

  if (!currentChat) {
    return (
      <div className="chat-area">
        <div className="empty-state">
          <div className="icon">💬</div>
          <h3>Выберите чат</h3>
          <p style={{ fontSize: '13px', color: '#555' }}>Начните общение с пользователем</p>
        </div>
      </div>
    );
  }

  const isOnline = isUserOnline ? isUserOnline(currentChat.username) : false;

  return (
    <div className="chat-area">
      <div className="chat-header">
        <div className="chat-user">
          {isMobile && (
            <button 
              className="mobile-chat-menu-btn"
              onClick={onOpenSidebar}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                fontSize: '24px',
                cursor: 'pointer',
                marginRight: '10px',
                padding: '5px 10px',
                borderRadius: '8px',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#6c5ce7';
                e.target.style.background = 'rgba(108, 92, 231, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#888';
                e.target.style.background = 'transparent';
              }}
              title="Список чатов"
            >
              ☰
            </button>
          )}
          <button 
            className="back-button"
            onClick={onCloseChat}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '24px',
              cursor: 'pointer',
              marginRight: '10px',
              padding: '5px 10px',
              borderRadius: '8px',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#6c5ce7';
              e.target.style.background = 'rgba(108, 92, 231, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#888';
              e.target.style.background = 'transparent';
            }}
            title="Выйти из чата"
          >
            ←
          </button>
          <div className="avatar">
            {chatAvatar && chatAvatar.startsWith('data:image') ? (
              <img 
                src={chatAvatar} 
                alt={currentChat.username}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (chatAvatar || '👤')}
          </div>
          <div>
            <div className="name">{currentChat.username}</div>
            <div className="status" style={{ 
              color: isOnline ? '#00b894' : '#666',
              transition: 'color 0.3s'
            }}>
              {isOnline ? '🟢 онлайн' : '🔴 оффлайн'}
            </div>
          </div>
        </div>
      </div>

      <div className="messages-area">
        {!messages || messages.length === 0 ? (
          <div className="empty-state">
            <div className="icon">💬</div>
            <h3>Нет сообщений</h3>
            <p style={{ fontSize: '13px', color: '#555' }}>Напишите первое сообщение</p>
          </div>
        ) : (
          messages.map(msg => {
            const isSent = msg.from === user?.username;
            const isEditing = editingMessage === msg.id;
            
            return (
              <div
                key={msg.id}
                className={`message ${isSent ? 'sent' : 'received'}`}
                onMouseEnter={() => setMenuVisible(msg.id)}
                onMouseLeave={() => {
                  if (!isEditing) setMenuVisible(null);
                }}
                style={{ position: 'relative' }}
              >
                {isEditing ? (
                  <div style={{ width: '100%' }}>
                    <input
                      ref={editInputRef}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(msg.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      style={{
                        width: '100%',
                        background: '#0a0a1a',
                        border: '2px solid #6c5ce7',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#e0e0e0',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleSaveEdit(msg.id)}
                        style={{
                          background: '#6c5ce7',
                          border: 'none',
                          color: '#fff',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        💾 Сохранить
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          background: '#2a2a4a',
                          border: 'none',
                          color: '#888',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ✖ Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="msg-text">{msg.text}</div>
                    <div className="msg-time">
                      {new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </>
                )}
                
                {!isEditing && canEditDelete(msg) && menuVisible === msg.id && (
                  <div 
                    className="message-menu"
                    style={{
                      position: 'absolute',
                      right: isSent ? '-45px' : 'auto',
                      left: isSent ? 'auto' : '-45px',
                      top: '5px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      background: '#1a1a2e',
                      borderRadius: '8px',
                      padding: '4px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                      zIndex: 10
                    }}
                  >
                    <button 
                      onClick={() => handleEditMessage(msg)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#6c5ce7',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(108, 92, 231, 0.2)'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={() => handleDeleteMessage(msg)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#e74c3c',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(231, 76, 60, 0.2)'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-input" onSubmit={handleSend}>
        <input
          type="text"
          placeholder="Введите сообщение..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <button type="submit" className="send-btn">➤</button>
      </form>
    </div>
  );
}

export default ChatArea;